import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogPosts } from '../mockData';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F9FBF5] pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Post Not Found</h2>
          <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FBF5] pt-20">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/blog')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Button>

        <article className="bg-white rounded-xl shadow-sm overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-96 object-cover"
          />
          <div className="p-8 md:p-12">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary">{tag}</Badge>
              ))}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-gray-600 mb-8 pb-8 border-b">
              <span className="font-medium">{post.author}</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {post.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                {post.excerpt}
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Artificial Intelligence is transforming the workplace at an unprecedented pace. From automating
                routine tasks to enabling new forms of creative work, AI is reshaping how we approach productivity
                and collaboration.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                In this comprehensive analysis, we examine data from over 1,000 companies and 50,000 professionals
                to understand the real impact of AI on productivity. Our findings reveal surprising insights about
                which industries are benefiting most, and where AI adoption is creating the biggest challenges.
              </p>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Key Findings</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our research identified three major trends in AI-driven productivity:
              </p>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                <li>Companies using AI tools report an average 35% increase in employee productivity</li>
                <li>AI adoption is highest in tech, finance, and healthcare sectors</li>
                <li>Professionals working with AI earn 28% more on average than their peers</li>
              </ul>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">The Future of AI in the Workplace</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As AI continues to evolve, we expect to see even more dramatic shifts in how work gets done.
                Companies that invest in AI talent and tools today will be best positioned to compete in tomorrow's
                market.
              </p>
              <p className="text-gray-700 leading-relaxed">
                For professionals, developing AI literacy and learning to work alongside AI systems will be
                essential for career growth and competitiveness.
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPostPage;
